import React, { useEffect, useRef, useState } from 'react'
import {
  Container,
  Button,
  Row,
  Col,
  Accordion,
  Card
} from 'react-angle-dashboard-components'
import { PaymentSource, isNomi, paymentSourceObject } from '@angle/utils'
import { ExpirationBanner, CardRow } from '../styles'
import { IconUpArrow } from '../../CptSearch/vector'
import { ClaimResult } from '../../../services/claims'
import { ClaimDetail } from './ClaimDetail'
import {
  GetClaimsByMemberReqBody,
  GetClaimsByNumberReqBody
} from '../../../services/claims'
import { TicketForm } from '../TicketForm'
import { NoResultTicketForm } from './NoResultTicketForm'

interface SearchResultsProps {
  claims: ClaimResult[] | undefined
  toggleSelect: any
  selected: any
  searchQuery: GetClaimsByNumberReqBody | GetClaimsByMemberReqBody
  clearDataTime: string
  formatForPrint: Boolean
  clearResults: (msg: string) => void
}

export const SearchResults = React.forwardRef<any, SearchResultsProps>(
  (
    {
      claims,
      toggleSelect,
      selected,
      searchQuery,
      clearDataTime,
      formatForPrint,
      clearResults
    },
    ref
  ) => {
    const submitIssueRef = useRef<HTMLHeadingElement>(null)
    const [isAddressIssue, setIsAddressIssue] = useState(false)

    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (scrollRef && scrollRef.current && claims) {
        scrollRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    }, [scrollRef, claims])

    const PrintOrAccordion = React.memo(({ children }: any) => {
      return (
        <>
          {formatForPrint ? (
            <>{children}</>
          ) : (
            <Accordion activeKey={selected}>{children}</Accordion>
          )}
        </>
      )
    })

    const PrintOrAccordionToggle = React.memo(({ children, claim }: any) => {
      const className = `border py-2 ${
        claim.claimId === selected ? 'bg-light rounded-top' : 'rounded'
      }`
      return (
        <>
          {formatForPrint ? (
            <Row className={className}>{children}</Row>
          ) : (
            <Accordion.Toggle
              role="button"
              eventKey={claim.claimId}
              as={Row}
              onClick={() => toggleSelect(claim.claimId)}
              className={className}
            >
              {children}
            </Accordion.Toggle>
          )}
        </>
      )
    })

    const PrintOrAccordionCollapse = React.memo(({ children, claim }: any) => {
      return (
        <>
          {formatForPrint ? (
            <>{children}</>
          ) : (
            <Accordion.Collapse eventKey={claim.claimId}>
              {children}
            </Accordion.Collapse>
          )}
        </>
      )
    })

    const onWrongAddressClick = () => {
      submitIssueRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      setIsAddressIssue(true)
    }

    return (
      <div>
        <ExpirationBanner className="mb-4">
          <h5>
            All information on this page will be removed from the screen
            automatically at {clearDataTime} local time
          </h5>
        </ExpirationBanner>
        {claims && claims?.length > 0 ? (
          <Container className="mt-4">
            <Row ref={ref} className="mx-0" style={{ flex: 1 }}>
              <Col xs={12} className="py-3 px-1">
                <Row
                  className="d-none d-sm-flex text-nowrap"
                  style={{
                    color: '#BDBDBD',
                    fontWeight: 500,
                    scrollMarginTop: 250
                  }}
                  ref={scrollRef}
                >
                  <Col xs="auto" className="mr-4">
                    Name / ID
                  </Col>
                  <Col>Patient Acct No.</Col>
                  <Col>Date of Service</Col>
                  <Col className="mr-5">Claim Number</Col>
                  <Col className="ml-5">Status</Col>
                  <Col>Billed Amount</Col>
                </Row>

                <div
                  style={{
                    background: 'transparent'
                  }}
                >
                  <PrintOrAccordion>
                    {claims?.map((claim: ClaimResult) => {
                      const paymentSourceTitle = isNomi(claim.paymentSource) ? "Payment Through" : claim.paymentSource === "lob_check" ? "Check Number" : "Check Number"
                      const paymentSourceContent = isNomi(claim.paymentSource) ? paymentSourceObject[claim.paymentSource as PaymentSource] :
                                                          claim.paymentSource === "lob_check" ? claim.checkNumber : claim.checkNumber

                      return (
                        <div className="my-3" key={claim.claimId}>
                          <PrintOrAccordionToggle claim={claim}>
                            <Col>
                              <h6 className="text-primary mb-1">
                                {claim.memberName}
                              </h6>
                              <div className="text-muted fs-12">
                                {claim.memberId}
                              </div>
                            </Col>
                            <Col className="d-flex align-items-center text-muted">
                              {claim.patientAccountNumber}
                            </Col>
                            <Col className="d-flex align-items-center text-muted">
                              {claim.dateOfService}
                            </Col>
                            <Col className="d-flex align-items-center text-muted text-nowrap">
                              {claim.claimNumber}
                            </Col>
                            <Col className="d-flex align-items-center text-muted">
                              {claim.anglePaidStatus}
                            </Col>
                            <Col className="d-flex align-items-center text-muted">
                              {claim.billedAmount}
                            </Col>
                            {!formatForPrint && (
                              <Col xs="2">
                                <Button
                                  variant="link"
                                  className="text-primary py-1 px-1"
                                  role="button"
                                >
                                  {claim.claimId === selected ? (
                                    <>
                                      <span className="d-none d-sm-inline">
                                        See Less
                                      </span>{' '}
                                      <IconUpArrow
                                        style={{ marginLeft: '12px' }}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <span className="d-none d-sm-inline">
                                        See More
                                      </span>{' '}
                                      <i
                                        style={{ top: 2, position: 'relative' }}
                                        className="ml-2 icon-down-arrow"
                                      />
                                    </>
                                  )}
                                </Button>
                              </Col>
                            )}
                          </PrintOrAccordionToggle>
                          <PrintOrAccordionCollapse claim={claim}>
                            <CardRow className="border py-3 rounded-bottom">
                              <ClaimDetail
                                title="Allowed Amount"
                                content={claim.allowedAmount}
                              />
                              <ClaimDetail
                                title="Network Savings"
                                content={claim.networkSavings}
                              />
                              <ClaimDetail
                                title="Member Responsibility"
                                content={claim.memberResponsibility}
                              />
                              <ClaimDetail
                                title="Angle Paid"
                                content={claim.anglePaidAmount}
                              />
                              <ClaimDetail
                                title={paymentSourceTitle}
                                content={paymentSourceContent}
                              />
                              <ClaimDetail
                                title="Paid Date"
                                content={claim.paidDate}
                              />
                              <Col className="px-1">
                                <Card className="p-3">
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'start'
                                    }}
                                  >
                                    <div>
                                      <small
                                        className="text-muted"
                                        style={{ whiteSpace: 'nowrap' }}
                                      >
                                        Check/RA Address Sent
                                      </small>
                                      <p>{claim.sentAddress || ' - '}</p>
                                    </div>
                                    <Button
                                      variant="link"
                                      style={{ margin: 0, padding: 0 }}
                                      onClick={onWrongAddressClick}
                                    >
                                      Wrong Address?
                                    </Button>
                                  </div>
                                </Card>
                              </Col>
                            </CardRow>
                          </PrintOrAccordionCollapse>
                        </div>
                      )
                    })}
                  </PrintOrAccordion>
                </div>
              </Col>
            </Row>
            <TicketForm
              searchQuery={searchQuery}
              ref={submitIssueRef}
              isAddressIssue={isAddressIssue}
              clearResults={clearResults}
            />
          </Container>
        ) : (
          <NoResultTicketForm
            clearResults={clearResults}
            searchQuery={searchQuery}
          />
        )}
      </div>
    )
  }
)
